/**
 * Drawer Component
 * 
 * A sliding drawer/panel component with backdrop overlay.
 * Slides in from the right (default) or left side.
 */

import { ReactNode, useEffect, useRef, useCallback, type AriaRole } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { lockBodyScroll, unlockBodyScroll } from '../utils/body-scroll-lock';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

const slideInRight = keyframes`
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
`;

const slideOutRight = keyframes`
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(100%);
  }
`;

const slideInLeft = keyframes`
  from {
    transform: translateX(-100%);
  }
  to {
    transform: translateX(0);
  }
`;

const slideOutLeft = keyframes`
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(-100%);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const fadeOut = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`;

interface BackdropProps {
  isClosing: boolean;
  backdropColor: string;
}

const Backdrop = styled.div<BackdropProps>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${({ backdropColor }) => backdropColor};
  z-index: 99998;
  animation: ${({ isClosing }) => (isClosing ? fadeOut : fadeIn)} 0.3s ease-out forwards;
`;

interface DrawerPanelProps {
  width: string;
  position: 'left' | 'right';
  isClosing: boolean;
  backgroundColor?: string;
}

const DrawerPanel = styled.div<DrawerPanelProps>`
  position: fixed;
  top: 0;
  ${({ position }) => (position === 'right' ? 'right: 0;' : 'left: 0;')}
  width: ${({ width }) => width};
  max-width: 100vw;
  height: 100vh;
  height: 100dvh;
  background-color: ${({ backgroundColor, theme }) => 
    backgroundColor || theme.colors?.background?.primary || '#ffffff'};
  z-index: 99999;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: ${({ position, theme }) =>
    position === 'right'
      ? theme.shadow?.md || '-4px 0 16px rgba(0, 0, 0, 0.1)'
      : theme.shadow?.md || '4px 0 16px rgba(0, 0, 0, 0.1)'};
  animation: ${({ position, isClosing }) => {
    if (position === 'right') {
      return isClosing ? slideOutRight : slideInRight;
    }
    return isClosing ? slideOutLeft : slideInLeft;
  }} 0.3s ease-out forwards;
`;

const DrawerContent = styled.div`
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

export interface DrawerProps {
  id?: string;
  isOpen: boolean;
  onClose?: () => void;
  onClickOutside?: () => void;
  position?: 'left' | 'right';
  width?: string;
  backdropColor?: string;
  backgroundColor?: string;
  children?: ReactNode;
  /** Accessible name when Drawer acts as a dialog (e.g. side cart). */
  'aria-label'?: string;
  role?: AriaRole;
  'aria-modal'?: boolean | 'true' | 'false';
}

export const Drawer = ({
  id,
  isOpen,
  onClose,
  onClickOutside,
  position = 'right',
  width = '470px',
  backdropColor = 'rgba(0, 0, 0, 0.5)',
  backgroundColor = '#ffffff',
  children,
  'aria-label': ariaLabel,
  role,
  'aria-modal': ariaModal,
}: DrawerProps) => {
  const isClosingRef = useRef(false);
  const drawerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  const isDialog = role === 'dialog';

  const getVisibleFocusables = useCallback((): HTMLElement[] => {
    if (!drawerRef.current) return [];
    const all = drawerRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
    return Array.from(all).filter((el) => {
      const styles = globalThis.getComputedStyle(el);
      return (
        styles.display !== 'none' &&
        styles.visibility !== 'hidden' &&
        styles.opacity !== '0'
      );
    });
  }, []);

  const focusFirstOrPanel = useCallback(() => {
    const panel = drawerRef.current;
    if (!panel) return;

    const focusables = getVisibleFocusables();
    if (focusables.length > 0) {
      focusables[0].focus({ preventScroll: true });
      return;
    }

    panel.focus({ preventScroll: true });
  }, [getVisibleFocusables]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    lockBodyScroll();
    return () => {
      unlockBodyScroll();
    };
  }, [isOpen]);

  // Capture trigger + auto-focus first focusable (or panel) when dialog opens
  useEffect(() => {
    if (!isOpen || !isDialog) return;

    triggerRef.current = document.activeElement as HTMLElement;

    const raf = requestAnimationFrame(() => {
      focusFirstOrPanel();
    });

    return () => cancelAnimationFrame(raf);
  }, [isOpen, isDialog, focusFirstOrPanel]);

  // When focusables mount after open (e.g. loading → content), move focus onto them
  // only if focus is still on the panel or outside the drawer.
  useEffect(() => {
    if (!isOpen || !isDialog) return;

    const panel = drawerRef.current;
    if (!panel) return;

    const observer = new MutationObserver(() => {
      const focusables = getVisibleFocusables();
      if (focusables.length === 0) return;

      const active = document.activeElement;
      const focusOnPanelOrOutside =
        active === panel ||
        active === document.body ||
        !(active instanceof Node) ||
        !panel.contains(active);

      if (focusOnPanelOrOutside) {
        focusables[0].focus({ preventScroll: true });
      }
    });

    observer.observe(panel, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [isOpen, isDialog, getVisibleFocusables]);

  // Restore focus to trigger when dialog closes
  useEffect(() => {
    if (!isDialog) return;
    if (isOpen) return;
    triggerRef.current?.focus({ preventScroll: true });
  }, [isOpen, isDialog]);

  // Keyboard: Escape + Tab trap (dialog mode)
  useEffect(() => {
    if (!isOpen) return;

    const handleKeydown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && onClose) {
        onClose();
        return;
      }

      if (isDialog && e.key === 'Tab') {
        const panel = drawerRef.current;
        const focusables = getVisibleFocusables();
        if (focusables.length === 0) {
          e.preventDefault();
          panel?.focus({ preventScroll: true });
          return;
        }

        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement;
        // Panel is tabIndex=-1 (not in focusables). Tab from panel/outside must re-enter cycle.
        const outsideCycle =
          active === panel ||
          !(active instanceof Node) ||
          !panel?.contains(active);

        if (e.shiftKey) {
          if (active === first || outsideCycle) {
            e.preventDefault();
            last.focus({ preventScroll: true });
          }
        } else if (active === last || outsideCycle) {
          e.preventDefault();
          first.focus({ preventScroll: true });
        }
      }
    };

    document.addEventListener('keydown', handleKeydown);
    return () => document.removeEventListener('keydown', handleKeydown);
  }, [isOpen, isDialog, onClose, getVisibleFocusables]);

  if (!isOpen) {
    return null;
  }

  const handleBackdropClick = () => {
    if (onClickOutside) {
      onClickOutside();
    } else if (onClose) {
      onClose();
    }
  };

  return (
    <>
      <Backdrop
        isClosing={isClosingRef.current}
        backdropColor={backdropColor}
        onClick={handleBackdropClick}
        data-testid="drawer-backdrop"
      />
      <DrawerPanel
        id={id}
        ref={drawerRef}
        width={width}
        position={position}
        isClosing={isClosingRef.current}
        backgroundColor={backgroundColor}
        data-testid="drawer-panel"
        role={role}
        aria-modal={ariaModal}
        aria-label={ariaLabel}
        tabIndex={isDialog ? -1 : undefined}
      >
        <DrawerContent>{children}</DrawerContent>
      </DrawerPanel>
    </>
  );
};

export default Drawer;

/* ---------- composable sub-components ------------------------------------ */

export const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.space.md} 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.color};
`;

export const DrawerTitle = styled.span`
  font-size: ${({ theme }) => theme.fontSizes[3]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const DrawerCloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: ${({ theme }) => theme.space.xs};
  color: ${({ theme }) => theme.colors.text.secondary};

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

export const DrawerBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: ${({ theme }) => theme.space.md} 20px;
  -webkit-overflow-scrolling: touch;
`;

export const DrawerFooter = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.space.md};
  padding: ${({ theme }) => theme.space.md} 20px;
  border-top: 1px solid ${({ theme }) => theme.colors.border.color};
`;

export const DrawerPrimaryButton = styled.button`
  flex: 1;
  padding: ${({ theme }) => theme.space.md};
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.demi};
  text-transform: uppercase;
  background: ${({ theme }) => theme.colors.pallete.secondary.color};
  color: ${({ theme }) => theme.colors.pallete.secondary.contrast};
  border: none;
  border-radius: ${({ theme }) => theme.radius.small};
  cursor: pointer;

  &:hover {
    opacity: 0.85;
  }
`;

export const DrawerOutlineButton = styled.button`
  flex: 1;
  padding: ${({ theme }) => theme.space.md};
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.demi};
  text-transform: uppercase;
  background: ${({ theme }) => theme.colors.background.primary};
  color: ${({ theme }) => theme.colors.text.primary};
  border: 2px solid ${({ theme }) => theme.colors.text.primary};
  border-radius: ${({ theme }) => theme.radius.small};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.background.secondary};
  }
`;

